/**
 * CreditChargeRequestsPage.tsx
 * Admin - 크레딧 충전 요청 관리 페이지
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface ChargeRequest {
  id: string;
  organization_id: string;
  organization_name: string;
  amount: number;
  vat: number;
  total_with_vat: number;
  payment_method: string;
  payment_status?: string;
  notes: string | null;
  reject_reason?: string;
  created_at: string;
  updated_at?: string;
}

type TabType = 'pending' | 'history';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: '2px solid #e5e7eb',
  },
  tab: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '-2px',
    position: 'relative' as const,
  },
  tabActive: {
    color: '#3b82f6',
    borderBottom: '2px solid #3b82f6',
  },
  badge: {
    marginLeft: '8px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: '#ef4444',
    color: '#fff',
    borderRadius: '10px',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '60px',
    color: '#6b7280',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '60px',
    color: '#9ca3af',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left' as const,
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#1f2937',
    borderBottom: '1px solid #f3f4f6',
  },
  amountCell: {
    textAlign: 'right' as const,
    fontFamily: 'monospace',
  },
  orgName: {
    fontWeight: '600',
    color: '#1f2937',
  },
  date: {
    fontSize: '13px',
    color: '#6b7280',
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
  },
  approveBtn: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  rejectBtn: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '12px',
  },
  statusCompleted: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  statusRejected: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '400px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '16px',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    minHeight: '100px',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
  modalBtn: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: '500',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  cancelBtn: {
    backgroundColor: '#e5e7eb',
    color: '#374151',
  },
  confirmBtn: {
    backgroundColor: '#ef4444',
    color: '#fff',
  },
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCurrency = (amount: number) => {
  return amount.toLocaleString() + '원';
};

export const CreditChargeRequestsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [pendingRequests, setPendingRequests] = useState<ChargeRequest[]>([]);
  const [historyRequests, setHistoryRequests] = useState<ChargeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  // 거절 모달
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<ChargeRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const fetchPendingRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/credits/requests/pending`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setPendingRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/credits/requests/history?limit=50`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setHistoryRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPendingRequests(), fetchHistory()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPendingRequests, fetchHistory]);

  const handleApprove = async (request: ChargeRequest) => {
    if (!window.confirm(`${request.organization_name}에 ${formatCurrency(request.amount)} 충전을 승인하시겠습니까?`)) {
      return;
    }

    setProcessing(request.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/credits/requests/${request.id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await response.json();

      if (data.success) {
        alert(`${request.organization_name}에 ${formatCurrency(request.amount)} 충전 완료!`);
        await Promise.all([fetchPendingRequests(), fetchHistory()]);
      } else {
        alert(data.detail || '승인 실패');
      }
    } catch (error) {
      console.error('Approve failed:', error);
      alert('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(null);
    }
  };

  const openRejectModal = (request: ChargeRequest) => {
    setRejectTarget(request);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectTarget) return;

    setProcessing(rejectTarget.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/credits/requests/${rejectTarget.id}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: rejectReason || null }),
      });
      const data = await response.json();

      if (data.success) {
        alert('충전 요청이 거절되었습니다.');
        setShowRejectModal(false);
        await Promise.all([fetchPendingRequests(), fetchHistory()]);
      } else {
        alert(data.detail || '거절 실패');
      }
    } catch (error) {
      console.error('Reject failed:', error);
      alert('거절 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <div style={styles.loading}>데이터를 불러오는 중...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>충전 요청 관리</h1>
        <p style={styles.subtitle}>Owner가 요청한 크레딧 충전을 승인/거절합니다</p>
      </div>

      {/* 탭 */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'pending' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('pending')}
        >
          대기 중
          {pendingRequests.length > 0 && (
            <span style={styles.badge}>{pendingRequests.length}</span>
          )}
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'history' ? styles.tabActive : {}),
          }}
          onClick={() => setActiveTab('history')}
        >
          처리 내역
        </button>
      </div>

      {/* 대기 중 탭 */}
      {activeTab === 'pending' && (
        pendingRequests.length === 0 ? (
          <div style={styles.emptyState}>대기 중인 충전 요청이 없습니다</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>독서실</th>
                <th style={{ ...styles.th, ...styles.amountCell }}>크레딧</th>
                <th style={{ ...styles.th, ...styles.amountCell }}>부가세</th>
                <th style={{ ...styles.th, ...styles.amountCell }}>입금액</th>
                <th style={styles.th}>요청일시</th>
                <th style={styles.th}>처리</th>
              </tr>
            </thead>
            <tbody>
              {pendingRequests.map((req) => (
                <tr key={req.id}>
                  <td style={styles.td}>
                    <div style={styles.orgName}>{req.organization_name}</div>
                    {req.notes && <div style={styles.date}>{req.notes}</div>}
                  </td>
                  <td style={{ ...styles.td, ...styles.amountCell }}>
                    {formatCurrency(req.amount)}
                  </td>
                  <td style={{ ...styles.td, ...styles.amountCell }}>
                    {formatCurrency(req.vat)}
                  </td>
                  <td style={{ ...styles.td, ...styles.amountCell, fontWeight: 'bold', color: '#059669' }}>
                    {formatCurrency(req.total_with_vat)}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.date}>{formatDate(req.created_at)}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.buttonGroup}>
                      <button
                        style={styles.approveBtn}
                        onClick={() => handleApprove(req)}
                        disabled={processing === req.id}
                      >
                        {processing === req.id ? '처리중...' : '승인'}
                      </button>
                      <button
                        style={styles.rejectBtn}
                        onClick={() => openRejectModal(req)}
                        disabled={processing === req.id}
                      >
                        거절
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {/* 처리 내역 탭 */}
      {activeTab === 'history' && (
        historyRequests.length === 0 ? (
          <div style={styles.emptyState}>처리 내역이 없습니다</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>독서실</th>
                <th style={{ ...styles.th, ...styles.amountCell }}>금액</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>처리일시</th>
                <th style={styles.th}>비고</th>
              </tr>
            </thead>
            <tbody>
              {historyRequests.map((req) => (
                <tr key={req.id}>
                  <td style={styles.td}>
                    <div style={styles.orgName}>{req.organization_name}</div>
                  </td>
                  <td style={{ ...styles.td, ...styles.amountCell }}>
                    {formatCurrency(req.amount)}
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        ...(req.payment_status === 'completed' ? styles.statusCompleted : styles.statusRejected),
                      }}
                    >
                      {req.payment_status === 'completed' ? '승인' : '거절'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.date}>{formatDate(req.updated_at || req.created_at)}</span>
                  </td>
                  <td style={styles.td}>
                    {req.reject_reason || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {/* 거절 모달 */}
      {showRejectModal && rejectTarget && (
        <div style={styles.modal} onClick={() => setShowRejectModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>충전 요청 거절</h2>
            <p style={{ marginBottom: '16px', color: '#6b7280' }}>
              {rejectTarget.organization_name}의 {formatCurrency(rejectTarget.amount)} 충전 요청을 거절합니다.
            </p>
            <textarea
              style={styles.textarea}
              placeholder="거절 사유 (선택)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div style={styles.modalButtons}>
              <button
                style={{ ...styles.modalBtn, ...styles.cancelBtn }}
                onClick={() => setShowRejectModal(false)}
              >
                취소
              </button>
              <button
                style={{ ...styles.modalBtn, ...styles.confirmBtn }}
                onClick={handleReject}
                disabled={processing === rejectTarget.id}
              >
                {processing === rejectTarget.id ? '처리중...' : '거절하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditChargeRequestsPage;
